import { GridColDef } from '@mui/x-data-grid';
import { MAX_ESTATE_DUTY_RATE, MIN_ESTATE_DUTY_RATE } from '../../fna/constants';
import DataGridEditBooleanCell, {
  DataGridEditBooleanCellProps,
} from '../components/dataGrid/dataGridEditBooleanCell';
import DataGridEditNumberCell, {
  formatNumber,
} from '../components/dataGrid/dataGridEditNumberCell';
import DataGridEditNumberSliderCell from '../components/dataGrid/dataGridEditNumberSliderCell';
import DataGridEditOptionsCell, {
  DataGridEditOptionsCellProps,
} from '../components/dataGrid/dataGridEditOptionsCell';

/**
 * Helper function to create a number column for DataGrid
 */
export const makeNumberColumn = (parameters: GridColDef): GridColDef => ({
  width: 100,
  editable: true,
  valueGetter: (value) => value || 0,
  valueFormatter: (value) => formatNumber(value),
  renderEditCell: DataGridEditNumberCell,
  ...parameters,
});

/**
 * Helper function to create a boolean column for DataGrid
 */
export const makeBooleanColumn = ({
  processRowUpdate,
  getCurrentBooleanValue,
  getFieldValueFromBoolean,
  disabled,
  ...parameters
}: GridColDef &
  Pick<
    DataGridEditBooleanCellProps,
    'processRowUpdate' | 'getCurrentBooleanValue' | 'getFieldValueFromBoolean' | 'disabled'
  >): GridColDef => ({
  width: 100,
  type: 'boolean',
  renderCell: (props) => (
    <DataGridEditBooleanCell
      {...props}
      disabled={disabled}
      processRowUpdate={processRowUpdate}
      getCurrentBooleanValue={getCurrentBooleanValue}
      getFieldValueFromBoolean={getFieldValueFromBoolean}
    />
  ),
  ...parameters,
});

/**
 * Helper function to create a column for DataGrid with options dropdown that is loaded from API
 */
export const makeAsyncOptionsColumn = ({
  processRowUpdate,
  optionsEndpoint,
  excludeOptionIds,
  ...parameters
}: GridColDef &
  Pick<
    DataGridEditOptionsCellProps,
    'processRowUpdate' | 'optionsEndpoint' | 'excludeOptionIds'
  >): GridColDef => ({
  width: 100,
  renderCell: (props) => (
    <DataGridEditOptionsCell
      {...props}
      excludeOptionIds={excludeOptionIds}
      optionsEndpoint={optionsEndpoint}
      processRowUpdate={processRowUpdate}
    />
  ),
  ...parameters,
});

/**
 * Helper function to create a estate duty columns for DataGrid
 */
export const makeEstateDutyColumns = (
  processRowUpdate: DataGridEditBooleanCellProps['processRowUpdate'],
  disabled?: boolean,
): GridColDef[] => [
  makeBooleanColumn({
    field: 'isEstateduty',
    headerName: 'Estate duty',
    processRowUpdate,
    disabled,
  }),
  {
    field: 'estatedutyRate',
    headerName: 'Estate duty rate',
    editable: true,
    width: 100,
    valueGetter: (value) => value || 0,
    renderCell: ({ value, row }) => (row.isEstateduty ? `${value}%` : ''),
    renderEditCell: (props) => (
      <DataGridEditNumberSliderCell
        {...props}
        min={MIN_ESTATE_DUTY_RATE}
        max={MAX_ESTATE_DUTY_RATE}
      />
    ),
  },
];
